import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * ReportViewer — sandboxed viewport for a generated visual report.
 *
 * The report is a self-contained HTML document served from a backend endpoint
 * that sets its OWN scoped, per-render nonce Content-Security-Policy. Everything
 * is inlined — the self-hosted Mermaid runtime and base64 `@font-face` fonts (no
 * CDN, no Google Fonts) — and only the backend's nonced scripts run. We embed it
 * in a `sandbox`ed iframe so those scripts run WITHOUT `allow-same-origin` — the
 * document can never read the app's cookies, storage, or DOM. `allow-popups`
 * lets links inside the report open in a new tab.
 *
 * Dumb: the `src` URL and `title` arrive as props; the smart wrapper builds the
 * tenant-scoped URL.
 */
export const ReportViewer = ({ src, title }: IProps): React.JSX.Element => {
  // EXTERNAL HOOKS
  const { i18n } = useLingui()

  // RENDER
  return (
    <iframe
      src={src}
      title={title ?? i18n._(msg`Visual report`)}
      sandbox="allow-scripts allow-popups"
      loading="lazy"
      className={classes.frame}
    />
  )
}
